import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, RefreshCw, Globe, Clock, Coins,
  Sword, Package, Mail, User as UserIcon, ShieldCheck, ArrowDownLeft, ArrowUpRight,
  Coins as CoinsIcon, Award, Save, Sparkles, Wand2, Feather,
  Trash2, Copy, RotateCcw, Search,
} from 'lucide-react';
import { ItemsView } from './ItemsView';
import { GuardLiveCard } from './GuardLiveCard';
import { useToast } from '@/shared/contexts/ToastContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import {
  getAdminCharacter,
  getCharacterEmails,
  editCharacter,
  applyCharacterSkillGm,
  setCharacterWings,
  setFullHonor,
  setFullMith,
  setFullRuna,
  setFullStellar,
  getClearStatus,
  clearCharacter,
  restoreCharacter,
  cloneValidate,
  cloneExecute,
  type CharacterDetailResponse,
  type CharacterRow,
  type EmailLogRow,
  type ClearTipo,
  type CloneValidateResult,
} from '@/shared/services/adminService';
import { CLASSES_PT, NATIONS_PT, formatPlaytime } from './labels';

type TabKey = 'itens' | 'emails';

const TABS: Array<{ key: TabKey; label: string; icon: any }> = [
  { key: 'itens',  label: 'Itens',  icon: Package },
  { key: 'emails', label: 'Emails', icon: Mail },
];

export default function CharacterDetailPage() {
  const { idx } = useParams<{ idx: string }>();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { hasFeature } = useAuth();
  const characterIdx = idx ? parseInt(idx, 10) : NaN;

  const [data, setData] = useState<CharacterDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('itens');

  const fetchData = useCallback(async () => {
    if (!characterIdx || Number.isNaN(characterIdx)) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminCharacter(characterIdx);
      setData(res);
    } catch (err: any) {
      const msg = err?.detail || err?.message || 'Erro ao carregar personagem';
      setError(msg);
      notify('error', msg);
    } finally {
      setLoading(false);
    }
  }, [characterIdx, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20 text-white/40">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="surface-card rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-400">Falha</p>
            <p className="text-xs text-white/65 mt-1">{error}</p>
          </div>
          <button
            onClick={() => navigate('/admin/characters')}
            className="px-3 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.08]
                       text-xs text-white/75 transition cursor-pointer"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const { char, userNum, accountId } = data;
  const online = char.login === 1;
  const nation = char.nation ? NATIONS_PT[char.nation] : null;
  const canEdit = hasFeature('edit_character');

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/characters"
            className="inline-flex items-center gap-1.5 text-xs text-white/55
                       hover:text-[#00e1d7] mb-2 transition"
          >
            <ArrowLeft size={14} />
            Voltar para Personagens
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            {char.name || `CharIdx ${char.characteridx}`}
            {online ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold
                               bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <ShieldCheck size={11} /> Online
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold
                               bg-white/[0.05] text-white/45 border border-white/[0.08]">
                Offline
              </span>
            )}
          </h1>
          <p className="text-sm text-white/65 mt-1 flex items-center flex-wrap gap-x-3 gap-y-1">
            <span className="font-mono text-white/45">CharIdx {char.characteridx}</span>
            <span>·</span>
            <Link
              to={`/admin/account/${userNum}`}
              className="inline-flex items-center gap-1 text-[#00e1d7] hover:underline"
            >
              <UserIcon size={12} />
              {accountId || `UserNum ${userNum}`}
            </Link>
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                     hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Header de info do char */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Nível" value={String(char.lev ?? '—')} icon={<Sword size={16} />} />
        <Stat label="Classe" value={CLASSES_PT[char.class as any] || `Cl ${char.class ?? '?'}`}
              icon={<UserIcon size={16} />} />
        <Stat label="Alz" value={(char.alz ?? 0).toLocaleString('pt-BR')} icon={<Coins size={16} />} />
        <Stat label="Playtime" value={char.playtime ? formatPlaytime(char.playtime) : '—'}
              icon={<Clock size={16} />} />
      </div>

      {/* Sub-info */}
      <div className="surface-card rounded-xl p-4 mb-6 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <Field label="Nação" value={
          nation ? (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold border ${nation.classes}`}>
              <Globe size={11} />
              {nation.name}
            </span>
          ) : <span className="text-white/35">—</span>
        } />
        <Field label="Overlord" value={char.ov ? <span className="text-amber-400 font-bold">OV {char.ov}</span> : <span className="text-white/35">—</span>} />
        <Field label="Guilda" value={char.guildname || <span className="text-white/35">—</span>} />
        <Field label="EXP" value={(char.exp ?? 0).toLocaleString('pt-BR')} mono />
        <Field label="PK" value={char.pkpenalty ? <span className="text-red-400 font-bold">{char.pkpenalty}</span> : <span className="text-white/35">0</span>} />
      </div>

      {/* Guard live + histórico in-memory */}
      <GuardLiveCard charIdx={char.characteridx} />

      {/* Container 1 — Itens: Equipamentos / Inventário / Emails */}
      <div className="surface-card rounded-xl overflow-hidden data-tbl">
        <div className="flex border-b border-white/[0.06]">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition cursor-pointer
                            ${active
                              ? 'text-[#00e1d7] border-[#00e1d7]'
                              : 'text-white/55 border-transparent hover:text-white'}`}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {tab === 'itens' && (
            // Container dividido: Equipamentos + Inventário lado a lado, dividindo o
            // espaço meio a meio (igual ao jogo). Empilha em telas menores que xl.
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:divide-x xl:divide-white/[0.06]">
              <div className="xl:pr-6 min-w-0">
                <ItemsView source="equipment" id={char.characteridx} classId={char.class} />
              </div>
              <div className="min-w-0">
                <ItemsView source="inventory" id={char.characteridx} />
              </div>
            </div>
          )}
          {tab === 'emails' && (
            <EmailsTab characterIdx={char.characteridx} />
          )}
        </div>
      </div>

      {/* Container 2 — Ações GM (direto na tela, abaixo dos itens) */}
      {canEdit && (
        <div className="surface-card rounded-xl overflow-hidden mt-6">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06]">
            <Wand2 size={15} className="text-[#00e1d7]" />
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider">Ações GM</h3>
          </div>
          <div className="p-5">
            <GmActionsTab char={char} userNum={userNum} online={online} onDone={fetchData} />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="surface-card rounded-xl p-3.5">
      <div className="flex items-center gap-2 text-[11px] text-white/55 uppercase tracking-wider font-bold mb-1.5">
        <span className="text-[#00e1d7]">{icon}</span>
        {label}
      </div>
      <p className="text-xl font-black text-white leading-none truncate">{value}</p>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-[11px] text-white/45 uppercase tracking-wider mb-1">{label}</p>
      <div className={`text-sm text-white/90 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EmailsTab — historico permanente (Server01.dbo._39_email_log)
// ─────────────────────────────────────────────────────────────────────────────
function EmailsTab({ characterIdx }: { characterIdx: number }) {
  const [items, setItems] = useState<EmailLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const pageSize = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCharacterEmails(characterIdx, page * pageSize, pageSize);
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar e-mails');
    } finally {
      setLoading(false);
    }
  }, [characterIdx, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (loading && items.length === 0) {
    return (
      <div className="py-12 flex items-center justify-center text-white/40">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg p-3 bg-red-500/10 border border-red-500/30 flex items-start gap-2">
        <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
        <p className="text-xs text-red-400">{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-12 text-center">
        <Mail size={32} className="text-white/20 mx-auto mb-3" />
        <p className="text-sm text-white/55 font-semibold">Sem e-mails registrados</p>
        <p className="text-xs text-white/40 mt-2 max-w-md mx-auto">
          O log de e-mails é populado por triggers nas tabelas nativas.
          Se acabou de aplicar a migration, novos e-mails serão capturados a partir de agora.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-white/55 mb-3">
        {total.toLocaleString('pt-BR')} e-mails registrados (log permanente)
      </p>
      <ul className="space-y-2">
        {items.map((m) => {
          const received = m.direction === 'R';
          const isOpen = expanded === m.logId;
          const otherParty = received
            ? `${m.senderName || '?'} (#${m.senderCharIdx})`
            : `${m.receiverName || '?'}${m.receiverCharIdx ? ` (#${m.receiverCharIdx})` : ''}`;
          const hasAttach = m.alz > 0 || m.itemKindIdx > 0;

          return (
            <li key={m.logId} className="bg-[#0d141b] border border-white/[0.04] rounded-lg overflow-hidden">
              <button
                onClick={() => setExpanded(isOpen ? null : m.logId)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition cursor-pointer text-left"
              >
                <span className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
                  received ? 'bg-emerald-500/10 text-emerald-400' : 'bg-sky-500/10 text-sky-400'
                }`}>
                  {received ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-white/65">
                      {received ? 'De:' : 'Para:'}
                    </span>
                    <span className="text-sm font-semibold text-white truncate">{otherParty}</span>
                    {hasAttach && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">
                        <CoinsIcon size={10} /> anexo
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/75 mt-0.5 truncate">{m.title || '(sem título)'}</p>
                </div>
                <span className="text-[10px] text-white/40 font-mono shrink-0">
                  {new Date(m.logDatetime).toLocaleString('pt-BR')}
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-3 pt-2 border-t border-white/[0.04] text-xs">
                  <p className="text-white/85 whitespace-pre-wrap break-words mb-3">
                    {m.content || <span className="text-white/30 italic">sem conteúdo</span>}
                  </p>
                  {hasAttach && (
                    <div className="flex flex-wrap gap-3 text-[11px] font-mono">
                      {m.alz > 0 && (
                        <span className="text-amber-400">
                          Alz: {m.alz.toLocaleString('pt-BR')}
                        </span>
                      )}
                      {m.itemKindIdx > 0 && (
                        <>
                          <span className="text-white/65">
                            Item: <span className="text-white">#{m.itemKindIdx}</span>
                          </span>
                          {m.itemOption !== 0 && (
                            <span className="text-white/55">
                              Opt: 0x{(m.itemOption >>> 0).toString(16).toUpperCase()}
                            </span>
                          )}
                          {m.itemDurationIdx > 0 && (
                            <span className="text-white/55">DurIdx: {m.itemDurationIdx}</span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-[11px] text-white/55">
          <span>Página {page + 1} de {totalPages}</span>
          <div className="flex gap-1.5">
            <button
              onClick={() => page > 0 && setPage((p) => p - 1)}
              disabled={page === 0 || loading}
              className="px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              Anterior
            </button>
            <button
              onClick={() => page + 1 < totalPages && setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages || loading}
              className="px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  GmActionsTab — aba "Ações GM" do personagem (migrado do bot Telegram)
// ─────────────────────────────────────────────────────────────────────────────
const CLEAR_TIPOS: Array<{ key: ClearTipo; label: string }> = [
  { key: 'inventario',  label: 'Inventário' },
  { key: 'equipamento', label: 'Equipamento' },
  { key: 'skilllist',   label: 'Lista de Skills' },
  { key: 'quickslot',   label: 'Atalhos (Quickslot)' },
  { key: 'tempinv',     label: 'Inventário Temp' },
  { key: 'questdata',   label: 'Quests' },
];

function GmActionsTab({ char, userNum, online, onDone }: {
  char: CharacterRow; userNum: number; online: boolean; onDone: () => Promise<void> | void;
}) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const ci = char.characteridx;
  const [rankVal, setRankVal] = useState('');
  const [nameVal, setNameVal] = useState('');
  const [stats, setStats] = useState({
    str: String(char.str ?? ''), dex: String(char.dex ?? ''),
    int: String(char.int ?? ''), pnt: String(char.pnt ?? ''),
  });
  const [wings, setWings] = useState({ grade: '4', level: '100' });
  const [nationVal, setNationVal] = useState(String(char.nation ?? 1));
  const [levelVal, setLevelVal] = useState(String(char.lev ?? ''));
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (key: string, fn: () => Promise<any>, okMsg: string) => {
    setBusy(key);
    try { await fn(); notify('success', okMsg); await onDone(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(null); }
  };

  const doHonra = async () => {
    const r = parseInt(rankVal, 10);
    if (!r || r < 1 || r > 20) { notify('error', 'Honra deve ser 1-20'); return; }
    if (!(await confirm({ title: 'Alterar Honra', description: `Setar Rank ${r} em ${char.name}?`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('honra', () => editCharacter(ci, { rank: r }), `Honra = ${r}`); setRankVal('');
  };
  const doRename = async () => {
    const nn = nameVal.trim();
    if (nn.length < 2 || nn.length > 16) { notify('error', 'Nome deve ter 2-16 caracteres'); return; }
    if (!(await confirm({ title: 'Renomear', description: `Renomear "${char.name}" para "${nn}"? (personagem precisa estar offline)`, tone: 'warning', confirmLabel: 'Renomear' }))) return;
    await run('rename', () => editCharacter(ci, { name: nn }), `Renomeado para ${nn}`); setNameVal('');
  };
  const doStats = async () => {
    const s = { str_: parseInt(stats.str, 10), dex: parseInt(stats.dex, 10), int_: parseInt(stats.int, 10), pnt: parseInt(stats.pnt, 10) };
    if ([s.str_, s.dex, s.int_, s.pnt].some((v) => Number.isNaN(v) || v < 0)) { notify('error', 'STR/DEX/INT/PNT devem ser números ≥ 0'); return; }
    if (!(await confirm({ title: 'Salvar Status', description: `STR=${s.str_} DEX=${s.dex} INT=${s.int_} PNT=${s.pnt} em ${char.name}?`, tone: 'warning', confirmLabel: 'Salvar' }))) return;
    await run('stats', () => editCharacter(ci, s), 'Status atualizado');
  };
  const doNation = async () => {
    const n = parseInt(nationVal, 10);
    if (n !== 1 && n !== 2) { notify('error', 'Nação inválida'); return; }
    const nome = n === 1 ? 'Capella' : 'Procyon';
    if (!(await confirm({ title: 'Mudar Nação', description: `Mudar ${char.name} para ${nome}? (personagem precisa estar offline)`, tone: 'danger', confirmLabel: `Mudar p/ ${nome}` }))) return;
    await run('nation', () => editCharacter(ci, { nation: n }), `Nação = ${nome}`);
  };
  const doLevel = async () => {
    const l = parseInt(levelVal, 10);
    if (!l || l < 1 || l > 999) { notify('error', 'Level deve ser 1-999'); return; }
    if (!(await confirm({ title: 'Mudar Level', description: `Setar Level ${l} em ${char.name}?`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('level', () => editCharacter(ci, { lev: l }), `Level = ${l}`);
  };
  const doGod = async () => {
    if (!(await confirm({ title: 'Godmode (Stats GM)', description: `STR/DEX/INT = 99999, PNT = 0 em ${char.name}?`, tone: 'danger', confirmLabel: 'Aplicar Godmode' }))) return;
    await run('god', () => editCharacter(ci, { str_: 99999, dex: 99999, int_: 99999, pnt: 0 }), 'Godmode aplicado');
  };
  const doSkill = async () => {
    if (!(await confirm({ title: 'Skills GM', description: `Aplicar o pacote de skills GM em ${char.name}? (personagem precisa estar offline)`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('skill', () => applyCharacterSkillGm(ci), 'Skills GM aplicadas');
  };
  const doWings = async () => {
    const g = parseInt(wings.grade, 10), l = parseInt(wings.level, 10);
    if (!g || g < 1 || g > 4) { notify('error', 'Grade deve ser 1-4'); return; }
    if (!l || l < 1 || l > 100) { notify('error', 'Level deve ser 1-100'); return; }
    if (!(await confirm({ title: 'Asas (ForceWing)', description: `Asas Grade ${g}, Level ${l} em ${char.name}? (offline)`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('wings', () => setCharacterWings(ci, { grade: g, level: l }), `Asas Grade ${g} Lv ${l}`);
  };
  const doFullHonor = async () => {
    if (!(await confirm({ title: 'Full Honra', description: `Aplicar honra completa (Reputation + medalhas) em ${char.name}? (offline)`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('honor', () => setFullHonor(ci, userNum), 'Honra completa aplicada');
  };
  const doFullMith = async () => {
    if (!(await confirm({ title: 'Full Mith', description: `Aplicar Mith completo em ${char.name}? (offline)`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('mith', () => setFullMith(ci), 'Mith completo aplicado');
  };
  const doFullRuna = async (tipo: 'espada' | 'magica') => {
    if (!(await confirm({ title: `Full Runa (${tipo})`, description: `Aplicar Runa completa (${tipo}) em ${char.name}? (offline)`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('runa', () => setFullRuna(ci, tipo), `Runa (${tipo}) aplicada`);
  };
  const doFullStellar = async () => {
    if (!(await confirm({ title: 'Full Stellar', description: `Aplicar Stellar completo em ${char.name}? (offline)`, tone: 'warning', confirmLabel: 'Aplicar' }))) return;
    await run('stellar', () => setFullStellar(ci), 'Stellar completo aplicado');
  };

  const [backups, setBackups] = useState<Set<string>>(new Set());
  const [cloneSrc, setCloneSrc] = useState(String(ci));
  const [cloneDst, setCloneDst] = useState('');
  const [cloneCheck, setCloneCheck] = useState<CloneValidateResult | null>(null);
  const [cloneBusy, setCloneBusy] = useState<'validate' | 'execute' | null>(null);

  const fetchClear = useCallback(async () => {
    try {
      const res = await getClearStatus(ci);
      setBackups(new Set(res.backups.map((b) => b.tipo)));
    } catch { /* silencioso — status é só conveniência */ }
  }, [ci]);
  useEffect(() => { fetchClear(); }, [fetchClear]);

  const doClear = async (tipo: ClearTipo, label: string) => {
    if (!(await confirm({ title: `Limpar ${label}`, description: `Apagar ${label} de ${char.name}? Um backup é salvo automaticamente (reversível). O personagem precisa estar offline.`, tone: 'danger', confirmLabel: 'Limpar' }))) return;
    await run(`clear-${tipo}`, () => clearCharacter(ci, tipo), `${label} limpo (backup salvo)`);
    await fetchClear();
  };
  const doRestore = async (tipo: ClearTipo, label: string) => {
    if (!(await confirm({ title: `Restaurar ${label}`, description: `Restaurar ${label} de ${char.name} a partir do backup?`, tone: 'warning', confirmLabel: 'Restaurar' }))) return;
    await run(`restore-${tipo}`, () => restoreCharacter(ci, tipo), `${label} restaurado`);
    await fetchClear();
  };

  const doCloneValidate = async () => {
    const s = parseInt(cloneSrc, 10), d = parseInt(cloneDst, 10);
    if (!s || !d) { notify('error', 'Informe origem e destino'); return; }
    if (s === d) { notify('error', 'Origem e destino não podem ser iguais'); return; }
    setCloneBusy('validate'); setCloneCheck(null);
    try {
      setCloneCheck(await cloneValidate(s, d));
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setCloneBusy(null); }
  };
  const doCloneExecute = async () => {
    if (!cloneCheck) return;
    const { src, dst } = cloneCheck;
    if (!(await confirm({ title: 'Clonar personagem', description: `SOBRESCREVER o destino "${dst.name}" (Lv.${dst.lev}) com TODOS os dados de "${src.name}" (Lv.${src.lev})? O destino perde o que tinha. Não há backup automático deste passo.`, tone: 'danger', confirmLabel: 'Clonar' }))) return;
    setCloneBusy('execute');
    try {
      const res = await cloneExecute(src.idx, dst.idx);
      notify('success', res.message || 'Clone concluído');
      setCloneCheck(null); setCloneDst('');
      await onDone();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setCloneBusy(null); }
  };

  const inputCls = 'w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white';
  const secTitle = 'text-[11px] font-bold text-white/50 uppercase tracking-wider mb-3';
  const fsBtn = 'px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-semibold text-sm hover:bg-emerald-500/25 transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2';

  return (
    <div>
      {online && (
        <p className="text-[11px] text-amber-400 mb-4 flex items-center gap-1.5">
          <AlertCircle size={12} /> Personagem online — as ações exigem estar offline.
        </p>
      )}

      {/* Atributos & Identidade */}
      <h4 className={secTitle}>Atributos &amp; Identidade</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Honra / Rank (1-20)</label>
          <div className="flex gap-2">
            <input type="text" inputMode="numeric" value={rankVal} onChange={(e) => setRankVal(e.target.value.replace(/\D/g, ''))} placeholder="1-20" className={`flex-1 ${inputCls}`} />
            <button onClick={doHonra} disabled={busy !== null || !rankVal} className="px-3 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/30 text-[#00e1d7] font-semibold text-sm hover:bg-[#00e1d7]/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5" title="Aplicar honra">
              {busy === 'honra' ? <Loader2 size={14} className="animate-spin" /> : <Award size={14} />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Renomear (2-16 chars)</label>
          <div className="flex gap-2">
            <input type="text" value={nameVal} maxLength={16} onChange={(e) => setNameVal(e.target.value)} placeholder={char.name || 'novo nome'} className={`flex-1 ${inputCls}`} />
            <button onClick={doRename} disabled={busy !== null || !nameVal.trim()} className="px-3 py-2 rounded-lg bg-violet-500/15 border border-violet-500/30 text-violet-400 font-semibold text-sm hover:bg-violet-500/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5" title="Renomear">
              {busy === 'rename' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            </button>
          </div>
        </div>
      </div>
      {/* Nação + Level */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Nação</label>
          <div className="flex gap-2">
            <select value={nationVal} onChange={(e) => setNationVal(e.target.value)} className={`flex-1 ${inputCls}`}>
              <option value="1">Capella</option>
              <option value="2">Procyon</option>
            </select>
            <button onClick={doNation} disabled={busy !== null} className="px-3 py-2 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-400 font-semibold text-sm hover:bg-fuchsia-500/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5" title="Mudar nação (offline)">
              {busy === 'nation' ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Level (1-999)</label>
          <div className="flex gap-2">
            <input type="text" inputMode="numeric" value={levelVal} onChange={(e) => setLevelVal(e.target.value.replace(/\D/g, ''))} placeholder={String(char.lev ?? '')} className={`flex-1 ${inputCls}`} />
            <button onClick={doLevel} disabled={busy !== null || !levelVal} className="px-3 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/30 text-[#00e1d7] font-semibold text-sm hover:bg-[#00e1d7]/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5" title="Mudar level">
              {busy === 'level' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {([['str', 'STR'], ['dex', 'DEX'], ['int', 'INT'], ['pnt', 'PNT']] as const).map(([k, lbl]) => (
          <div key={k}>
            <span className="text-[10px] text-white/40 uppercase tracking-wider">{lbl}</span>
            <input type="text" inputMode="numeric" value={(stats as any)[k]} onChange={(e) => setStats((s) => ({ ...s, [k]: e.target.value.replace(/\D/g, '') }))} placeholder="0" className={`${inputCls} mt-0.5`} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={doStats} disabled={busy !== null} className="px-4 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/30 text-[#00e1d7] font-semibold text-sm hover:bg-[#00e1d7]/25 transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
          {busy === 'stats' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar Status
        </button>
        <button onClick={doGod} disabled={busy !== null} className="px-4 py-2 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 font-semibold text-sm hover:bg-amber-500/25 transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2" title="STR/DEX/INT=99999, PNT=0">
          {busy === 'god' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Godmode (99999)
        </button>
      </div>
      <p className="text-[10px] text-white/35 mt-2">Para <b>tirar</b> o Godmode, edite os valores e clique em Salvar Status.</p>

      {/* Sets completos */}
      <h4 className={`${secTitle} mt-6 pt-4 border-t border-white/[0.06]`}>Sets completos</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Skills GM (pacote completo)</label>
          <button onClick={doSkill} disabled={busy !== null} className="w-full px-4 py-2 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-400 font-semibold text-sm hover:bg-fuchsia-500/25 transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy === 'skill' ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Aplicar Skills GM
          </button>
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Asas (Grade 1-4 / Level 1-100)</label>
          <div className="flex gap-2">
            <select value={wings.grade} onChange={(e) => setWings((w) => ({ ...w, grade: e.target.value }))} className={`${inputCls} w-20`}>
              {[1, 2, 3, 4].map((g) => <option key={g} value={g}>G{g}</option>)}
            </select>
            <input type="text" inputMode="numeric" value={wings.level} onChange={(e) => setWings((w) => ({ ...w, level: e.target.value.replace(/\D/g, '') }))} placeholder="Level" className={`flex-1 ${inputCls}`} />
            <button onClick={doWings} disabled={busy !== null} className="px-3 py-2 rounded-lg bg-sky-500/15 border border-sky-500/30 text-sky-400 font-semibold text-sm hover:bg-sky-500/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5" title="Aplicar asas">
              {busy === 'wings' ? <Loader2 size={14} className="animate-spin" /> : <Feather size={14} />}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
        <button onClick={doFullHonor} disabled={busy !== null} className={fsBtn}>
          {busy === 'honor' ? <Loader2 size={14} className="animate-spin" /> : <Award size={14} />} Full Honra
        </button>
        <button onClick={doFullMith} disabled={busy !== null} className={fsBtn}>
          {busy === 'mith' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Full Mith
        </button>
        <button onClick={doFullStellar} disabled={busy !== null} className={fsBtn}>
          {busy === 'stellar' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Full Stellar
        </button>
        <button onClick={() => doFullRuna('espada')} disabled={busy !== null} className={fsBtn}>
          {busy === 'runa' ? <Loader2 size={14} className="animate-spin" /> : <Sword size={14} />} Runa Espada
        </button>
        <button onClick={() => doFullRuna('magica')} disabled={busy !== null} className={fsBtn}>
          {busy === 'runa' ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Runa Mágica
        </button>
      </div>

      {/* Limpeza */}
      <h4 className={`${secTitle} mt-6 pt-4 border-t border-white/[0.06]`}>Limpeza (reversível — backup automático)</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {CLEAR_TIPOS.map(({ key, label }) => {
          const hasBkp = backups.has(key);
          return (
            <div key={key} className="flex items-center gap-2 bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2">
              <span className="flex-1 text-sm text-white/80">{label}</span>
              {hasBkp && (
                <button onClick={() => doRestore(key, label)} disabled={busy !== null}
                  className="px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs hover:bg-emerald-500/20 transition cursor-pointer inline-flex items-center gap-1 disabled:opacity-50" title="Restaurar do backup">
                  {busy === `restore-${key}` ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restaurar
                </button>
              )}
              <button onClick={() => doClear(key, label)} disabled={busy !== null}
                className="px-2.5 py-1 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-xs hover:bg-red-500/20 transition cursor-pointer inline-flex items-center gap-1 disabled:opacity-50" title="Limpar (com backup)">
                {busy === `clear-${key}` ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Limpar
              </button>
            </div>
          );
        })}
      </div>

      {/* Clonar */}
      <h4 className={`${secTitle} mt-6 pt-4 border-t border-white/[0.06]`}>Clonar personagem</h4>
      <p className="text-[11px] text-white/45 mb-3">Copia todos os dados da ORIGEM para o DESTINO (o destino é sobrescrito e precisa estar offline).</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Origem (idx)</label>
          <input type="text" inputMode="numeric" value={cloneSrc} onChange={(e) => { setCloneSrc(e.target.value.replace(/\D/g, '')); setCloneCheck(null); }} placeholder="idx origem" className={inputCls} />
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Destino (idx) — será sobrescrito</label>
          <input type="text" inputMode="numeric" value={cloneDst} onChange={(e) => { setCloneDst(e.target.value.replace(/\D/g, '')); setCloneCheck(null); }} placeholder="idx destino" className={inputCls} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={doCloneValidate} disabled={cloneBusy !== null || !cloneSrc || !cloneDst}
          className="px-4 py-2 rounded-lg bg-sky-500/15 border border-sky-500/30 text-sky-400 font-semibold text-sm hover:bg-sky-500/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-2">
          {cloneBusy === 'validate' ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Verificar
        </button>
        {cloneCheck && (
          <button onClick={doCloneExecute} disabled={cloneBusy !== null}
            className="px-4 py-2 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 font-semibold text-sm hover:bg-red-500/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {cloneBusy === 'execute' ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Executar clone
          </button>
        )}
      </div>
      {cloneCheck && (
        <div className="mt-3 text-xs bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-white/75">
          <span className="text-[#00e1d7] font-semibold">{cloneCheck.src.name}</span> (Lv.{cloneCheck.src.lev}, idx {cloneCheck.src.idx})
          {' → '}
          <span className="text-amber-400 font-semibold">{cloneCheck.dst.name}</span> (Lv.{cloneCheck.dst.lev}, idx {cloneCheck.dst.idx})
          <span className="text-white/45"> — confira e clique em Executar clone.</span>
        </div>
      )}
    </div>
  );
}

